import { action, makeAutoObservable } from 'mobx';

import useStore from '@/common/hooks/useStore';
import { PinnedItem } from '@/forwarding/entities/PinnedItem/PinnedItem';
import { PinnedProduct } from '@/forwarding/entities/PinnedItem/PinnedProduct';
import PinnedItemsStorage from '@/forwarding/services/PinnedItemsStorage';
import { BaseProductType } from '@/product/constants/productTypes';
import { ProductId } from '@/product/entity/Product';

class PinnedItemsStore {
  pinnedItems: PinnedItem[] = PinnedItemsStorage.get();

  private get itemsIds() {
    return new Set(this.pinnedItems.map((item) => item.id));
  }

  constructor() {
    makeAutoObservable(this, {
      isPinned: false,
      getProductsByType: false,
      hasSameProducts: false,
      switchPin: false,
    });

    PinnedItemsStorage.onKeyChange(
      action((value) => {
        this.pinnedItems = value;
      }),
    );
  }

  isPinned = (pinnedItem: PinnedItem) => {
    return this.itemsIds.has(pinnedItem.id);
  };

  pin = (item: PinnedItem) => {
    this.pinAll([item]);
  };

  pinAll = (items: PinnedItem[]) => {
    const pinnedIds = new Set(items.map((item) => item.id));
    const restItems = this.pinnedItems.filter((item) => !pinnedIds.has(item.id));

    this.pinnedItems = [...items, ...restItems];

    PinnedItemsStorage.save(this.pinnedItems);
  };

  unpin = (id: StringOrNumber) => {
    this.pinnedItems = this.pinnedItems.filter((pinnedItem) => pinnedItem.id !== id);
    PinnedItemsStorage.save(this.pinnedItems);
  };

  getProductsByType = (baseProductType: BaseProductType): PinnedProduct[] => {
    return this.pinnedItems.filter(
      (item): item is PinnedProduct => item instanceof PinnedProduct && item.productType === baseProductType,
    );
  };

  hasSameProducts = (productIds: ProductId[], baseProductType: BaseProductType) => {
    const uniqueProductIds = new Set(productIds);

    if (uniqueProductIds.size !== productIds.length) {
      return false;
    }

    const pinnedProductIds = new Set(this.getProductsByType(baseProductType).map((item) => item.productId));

    return pinnedProductIds.size === uniqueProductIds.size && productIds.every((id) => pinnedProductIds.has(id));
  };

  reorderProducts = (productIds: ProductId[], baseProductType: BaseProductType) => {
    if (!this.hasSameProducts(productIds, baseProductType)) {
      return;
    }

    const pinnedProductsById = new Map(this.getProductsByType(baseProductType).map((item) => [item.productId, item]));
    let orderIndex = 0;

    this.pinnedItems = this.pinnedItems.map((item) => {
      if (!(item instanceof PinnedProduct) || item.productType !== baseProductType) {
        return item;
      }

      const reorderedItem = pinnedProductsById.get(productIds[orderIndex]!)!;

      orderIndex += 1;

      return reorderedItem;
    });

    PinnedItemsStorage.save(this.pinnedItems);
  };

  switchPin = (item: PinnedItem) => {
    if (this.isPinned(item)) {
      this.unpin(item.id);
    } else {
      this.pin(item);
    }
  };

  unpinAll = () => {
    this.pinnedItems = [];
    PinnedItemsStorage.save(this.pinnedItems);
  };
}

export const usePinnedItemsStore = () => useStore(PinnedItemsStore);

export const pinnedItemsStore = new PinnedItemsStore();

export default PinnedItemsStore;
